<template>
  <div class="segmented-select">
    <popper
      ref="popper"
      trigger="focus"
      :options="{
        placement: 'bottom-start',
        arrowElement: null,
        modifiers: {
          arrow: {
            enabled: false,
          },
          preventOverflow: {
            enabled: false
          },
          flip: {
            enabled: false,
          },
        },
      }"
      :disabled="disabled"
      root-class="select-popper"
      @show="onPopperShow()"
      @hide="onPopperHide()"
      @documentClick="closePopper()"
    >
      <transition name="fade">
        <segmented-list
          v-show="state.popperShown"
          ref="segmentedList"
          :items="filteredItems"
          :segment-by="state.searchTerm ? undefined : segmentBy"
          :multi-column="!state.searchEnabled || !state.searchTerm || filteredItems.length >= 7"
          :continuous-columns="continuousColumns"
          :row-count="rowCount"
          :highlight-term="state.searchEnabled ? state.searchTerm : null"
          :class="{
            'no-segment': !!state.searchTerm
          }"
          @click="(item) => onItemClick(item)"
          @keydown.down.native="onDownKey()"
          @keydown.up.native="onUpKey()"
          @keydown.right.native="onRightKey()"
          @keydown.left.native="onLeftKey()"
          @keydown.esc.native="closePopper()"
        />
      </transition>
      <div slot="reference" @click="(event) => openPopperOrSearch(event)">
        <md-field ref="popperRef" class="search-field">
          <md-icon v-if="state.searchEnabled" class="search-icon" @click.native.stop="toggleSearch()">
            arrow_back
          </md-icon>
          <div
            v-if="!state.searchEnabled || disabled"
            class="search-readonly"
            :style="{ color: selectedItem ? 'black' : 'gray' }"
          >
            {{ selectedItem ? selectedItem.label : placeholder }}
          </div>
          <md-input
            v-if="state.searchEnabled && !disabled"
            ref="searchInput"
            class="search-input"
            :placeholder="state.searchEnabled ? 'Search' : placeholder"
            :value="
              state.searchEnabled
                ? state.searchTerm
                : selectedItem
                  ? selectedItem.label
                  : ''
            "
            @input="(value) => !selectedItem || value !== selectedItem.label ? updateSearchTerm(value) : null"
          />
          <md-icon v-if="!state.searchEnabled" class="search-icon" @click.native="toggleSearch()">
            search
          </md-icon>
          <md-icon>arrow_drop_down</md-icon>
        </md-field>
      </div>
    </popper>
  </div>
</template>

<script>
import Popper from 'vue-popperjs';
import 'vue-popperjs/dist/vue-popper.css';
import Fuse from 'fuse.js';
import sortBy from 'lodash/sortBy';
import last from 'lodash/last';

import SegmentedList from './SegmentedList.vue';

export default {
  name: 'segmented-select',
  components: { Popper, SegmentedList },
  props: {
    items: {
      type: Array,
      required: true,
    },
    segmentBy: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Select',
    },
    value: {
      type: String,
      default: null,
    },
    continuousColumns: {
      type: Boolean,
      default: false,
    },
    rowCount: {
      type: Number,
      default: 10,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        searchEnabled: false,
        searchTerm: '',
        popperShown: false,
      },
    };
  },
  computed: {
    itemsWithClasses() {
      return this.items.map((item) =>
        item === this.selectedItem
          ? {
              ...item,
              class: `${item.class} selected`,
              label: item.dropdownLabel || item.label,
            }
          : {
              ...item,
              label: item.dropdownLabel || item.label,
            }
      );
    },
    filteredItems() {
      if (!this.state.searchEnabled || !this.state.searchTerm) {
        return this.itemsWithClasses;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.fuse =
        this.fuse ||
        new Fuse(this.itemsWithClasses, {
          keys: ['label'],
          distance: 200,
          threshold: 0.3,
        });

      if (this.$refs.popper && this.$refs.popper.popperJS) {
        this.$refs.popper.popperJS.scheduleUpdate();
      }
      const filteredItems = this.fuse.search(this.state.searchTerm);
      return sortBy(filteredItems, filteredItem => filteredItem.label.toLowerCase());
    },
    selectedItem() {
      return this.items.find((item) => item.value === this.value);
    },
    itemKeys() {
      return this.items.map(item => item.key);
    },
  },
  watch: {
    'state.searchEnabled'(newVal, oldVal) {
      // Reset search when its toggled
      if (newVal && !oldVal) {
        this.state.searchTerm = '';
      }
    },
  },
  mounted() {
    this.documentClickHandler = (e) => {
      const $popperEl = this.$refs.popper && this.$refs.popper.$el;
      const $popperRefEl = this.$refs.popperRef && this.$refs.popperRef.$el;
      if (
        !$popperEl ||
        !$popperRefEl ||
        this.elementContains($popperEl, e.target) ||
        this.elementContains($popperRefEl, e.target)
      ) {
        return;
      }

      this.closePopper();
    };
    document.addEventListener('click', this.documentClickHandler);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.documentClickHandler);
  },
  methods: {
    toggleSearch(toValue = !this.state.searchEnabled) {
      if (this.disabled) {
        return;
      }
      this.state.searchEnabled = toValue;
      this.$nextTick(() => {
        if (
          this.state.searchEnabled &&
          this.$refs.searchInput &&
          this.$refs.searchInput.$el
        ) {
          this.$refs.searchInput.$el.focus();
        }
      });
    },
    togglePopper(event) {
      if (event.target.classList.contains('search-icon')) {
        this.$refs.popper.doShow();
        return;
      }
      this.$refs.popper.doToggle();
    },
    showPopper() {
      this.$refs.popper.doShow();
    },
    closePopper() {
      this.$refs.popper.doClose();
    },
    openPopperOrSearch() {
      if (!this.state.popperShown) {
        this.showPopper();
      } else if (!this.state.searchEnabled) {
        this.toggleSearch(true);
      }
    },
    updateSearchTerm(value) {
      this.state.searchTerm = value;
    },
    onItemClick(item) {
      this.$emit('select', item);
      this.toggleSearch(false);
      if (this.$refs.popper) {
        this.$refs.popper.doClose();
      }
    },
    onPopperShow() {
      this.state.popperShown = true;
      if (!this.state.searchEnabled) {
        this.$nextTick(() => {
          const selectedItem = this.$el.querySelector('.item:not(.header).selected') || this.$el.querySelector('.item:not(.header)');
          selectedItem.focus();
        });
      }

      // When the popper is used in a scaled container, its vertical position does not
      // take into account the scaling of the container and overlaps the reference
      // element. We adjust it with the height difference (normal height - scaled height) using margins
      const $popperRefEl = this.$refs.popperRef && this.$refs.popperRef.$el;
      const $segmentedListEl = this.$refs.segmentedList && this.$refs.segmentedList.$el;
      if ($popperRefEl && $segmentedListEl) {
        const scaledHeightDiff = $popperRefEl.offsetHeight - $popperRefEl.getBoundingClientRect().height;
        $segmentedListEl.style.marginTop = `${scaledHeightDiff}px`;
      }
    },
    onPopperHide() {
      this.state.popperShown = false;
      this.toggleSearch(false);
    },
    onDownKey() {
      const focusedItem = this.getFocusedItemElement();
      if (!focusedItem) {
        return;
      }
      if (focusedItem.nextElementSibling) {
        focusedItem.nextElementSibling.focus();
        return;
      }
      const nextGroup = focusedItem.parentElement.nextElementSibling;
      if (!nextGroup) {
        return;
      }
      // There is the segment header
      const nextGroupFirstItem = nextGroup.childNodes[1];
      if (!nextGroupFirstItem) {
        return;
      }
      nextGroupFirstItem.focus();
    },
    onUpKey() {
      const focusedItem = this.getFocusedItemElement();
      if (!focusedItem) {
        return;
      }
      if (focusedItem.previousElementSibling && !focusedItem.previousElementSibling.classList.contains('header')) {
        focusedItem.previousElementSibling.focus();
        return;
      }
      const previousGroup = focusedItem.parentElement.previousElementSibling;
      if (!previousGroup) {
        return;
      }
      const previousGroupFirstChild = last(previousGroup.childNodes);
      if (!previousGroupFirstChild) {
        return;
      }
      previousGroupFirstChild.focus();
    },
    onRightKey() {
      const itemsPerColumn = 10;
      const allItems = [...this.$el.querySelectorAll('.item')];
      const focusedItem = this.getFocusedItemElement();
      const focusedItemIndex = focusedItem ? allItems.indexOf(focusedItem) : 0;
      let targetItemIndex = focusedItemIndex + itemsPerColumn;
      let targetItem = this.getItemElementAt(targetItemIndex);
      if (!targetItem) {
        return;
      }
      if (!this.continuousColumns && targetItem.parentElement !== focusedItem.parentElement) {
        const focusedItemGroup = focusedItem.parentElement;
        const focusedItemGroupLength = focusedItemGroup.childNodes.length;
        const excessItemsCount = focusedItemGroupLength % itemsPerColumn;
        const emptyItemsCount = excessItemsCount === 0 ? 0 : itemsPerColumn % excessItemsCount;
        targetItemIndex -= emptyItemsCount;
        targetItem = this.getItemElementAt(targetItemIndex);
      }
      targetItem.focus();
    },
    onLeftKey() {
      const itemsPerColumn = 10;
      const allItems = [...this.$el.querySelectorAll('.item')];
      const focusedItem = this.getFocusedItemElement();
      const focusedItemIndex = focusedItem ? allItems.indexOf(focusedItem) : 0;
      let targetItemIndex = focusedItemIndex - itemsPerColumn;
      let targetItem = this.getItemElementAt(targetItemIndex);
      if (!targetItem) {
        return;
      }
      if (!this.continuousColumns && targetItem.parentElement !== focusedItem.parentElement) {
        const targetItemGroup = targetItem.parentElement;
        const targetItemGroupLength = targetItemGroup.childNodes.length;
        const excessItemsCount = targetItemGroupLength % itemsPerColumn;
        const emptyItemsCount = excessItemsCount === 0 ? 0 : itemsPerColumn % excessItemsCount;
        targetItemIndex += emptyItemsCount;
        targetItem = this.getItemElementAt(targetItemIndex);
      }
      targetItem.focus();
    },
    getFocusedItemElement() {
      return this.$el.querySelector('.item.md-focused') || this.$el.querySelector('.item.selected') || this.$el.querySelector('.item:not(.header)');
    },
    getItemElementAt(index) {
      const allItems = [...this.$el.querySelectorAll('.item')];
      if (index <= 0) {
        return allItems[0];
      }
      if (index >= allItems.length) {
        return allItems[allItems.length - 1];
      }
      return allItems[index];
    },
    elementContains(elm, otherElm) {
      if (typeof elm.contains === 'function') {
        return elm.contains(otherElm);
      }

      return false;
    },
  },
};
</script>

<style scoped>
.segmented-select {
  position: relative;
}

.search-field {
  background-color: white;
  border-radius: 5px;
  /* margin-right: 5px; */
  padding: 2px 2px;
  border: 1px solid #c4c4c4;
}

.search-input {
  border: none !important;
  cursor: pointer !important;
  margin: 0 !important;
  font-size: 14px;
  min-width: 0;
}

.search-readonly {
  flex: 1;
  padding: 6px 10px;
  font-size: 14px;
  text-align: left;
}

.segmented-select >>> .item.selected {
  color: #1d50a8;
}

.segmented-select >>> .md-button:before {
  transition: none;
}

.segmented-select >>> .popper__arrow {
  display: none;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
